import {
  ConversationTypeEnum,
  LeadStatusType,
  ReplyOption
} from "@coxauto-ui/cxm/communications/interfaces";
import {
  SignatureApiErrorType,
  VehicleApiErrorType
} from "@coxauto-ui/cxm/communications/api";
import {
  ComplianceOptedOutAlert,
  EmojiPickerErrorAlert,
  InactiveLeadWarningAlert,
  LeadStatusErrorAlert,
  MediaEndlinkErrorAlert,
  RepairOrderPriorityAlert,
  RepairOrderTextingDisabledAlert,
  SignatureInsertErrorAlert,
  TextOptInPendingAlert,
  TokenReplacementWarningAlert,
  VehicleInsertErrorAlert
} from "./reply-alerts";

export interface ReplyAlertsBottomContainerProps {
  // Conversation type
  conversationType: ConversationTypeEnum;

  // Compliance and send status
  selectedReplyOption: ReplyOption | null;
  messageFailedToSend: boolean;
  messageSentResponseFailedMsg: string;

  // ID-related alerts
  autoLeadId: string | null;
  repairOrderId: string | null;

  // Lead status
  autoLeadStatus: LeadStatusType;
  leadStatusError: boolean;

  // UI errors
  emojiPickerError: boolean;

  // Template warnings (optional - only for email/text)
  showTokenReplacementWarning?: boolean;
  onCloseTokenWarning?: () => void;

  // Opt-in status (TEXT only)
  isTextOptInPending?: boolean;

  // Signature insert error (EMAIL only)
  signatureInsertError?: SignatureApiErrorType | null;
  onDismissSignatureError?: () => void;

  // Vehicle insert error
  vehicleInsertError?: { label: string; errorType: VehicleApiErrorType } | null;
  onDismissVehicleError?: () => void;

  // Media endlink error
  endlinkError?: string | null;
  onDismissEndlinkError?: () => void;
}

/**
 * Centralized container for all reply-related alerts.
 * Renders alerts in a consistent order across all conversation types.
 */
export function ReplyAlertsBottomContainer({
  conversationType,
  selectedReplyOption,
  messageFailedToSend,
  messageSentResponseFailedMsg,
  autoLeadId,
  repairOrderId,
  autoLeadStatus,
  leadStatusError,
  emojiPickerError,
  showTokenReplacementWarning,
  onCloseTokenWarning,
  isTextOptInPending,
  signatureInsertError,
  onDismissSignatureError,
  vehicleInsertError,
  onDismissVehicleError,
  endlinkError,
  onDismissEndlinkError
}: ReplyAlertsBottomContainerProps) {
  return (
    <>
      {/* Opt-In Pending Alert (TEXT only) */}
      {conversationType === ConversationTypeEnum.TEXT && (
        <TextOptInPendingAlert isTextOptInPending={!!isTextOptInPending} />
      )}

      {/* Compliance/Send Failure Alert */}
      <ComplianceOptedOutAlert
        conversationType={conversationType}
        messageFailedToSend={messageFailedToSend}
        messageSentResponseFailedMsg={messageSentResponseFailedMsg}
        selectedReplyOption={selectedReplyOption}
      />

      {/* Repair Order Texting Disabled Alert (TEXT only) */}
      {conversationType === ConversationTypeEnum.TEXT && (
        <RepairOrderTextingDisabledAlert
          autoLeadId={autoLeadId}
          repairOrderId={repairOrderId}
        />
      )}

      {/* Inactive Lead Warning */}
      <InactiveLeadWarningAlert autoLeadStatus={autoLeadStatus} />

      {/* Lead Status Error */}
      <LeadStatusErrorAlert leadStatusError={leadStatusError} />

      {/* Emoji Picker Error */}
      <EmojiPickerErrorAlert emojiPickerError={emojiPickerError} />

      {/* Token Replacement Warning (EMAIL/TEXT only) */}
      {showTokenReplacementWarning !== undefined &&
        onCloseTokenWarning !== undefined && (
          <TokenReplacementWarningAlert
            showTokenReplacementWarning={showTokenReplacementWarning}
            onClose={onCloseTokenWarning}
          />
        )}

      {/* Repair Order Priority Alert */}
      <RepairOrderPriorityAlert
        autoLeadId={autoLeadId}
        repairOrderId={repairOrderId}
      />

      {/* Vehicle Insert Error */}
      {vehicleInsertError && onDismissVehicleError && (
        <VehicleInsertErrorAlert
          errorType={vehicleInsertError.errorType}
          vehicleLabel={vehicleInsertError.label}
          onClose={onDismissVehicleError}
        />
      )}

      {/* Media Endlink Error */}
      {onDismissEndlinkError && (
        <MediaEndlinkErrorAlert
          endlinkError={endlinkError ?? null}
          onClose={onDismissEndlinkError}
        />
      )}

      {/* Signature Insert Error (EMAIL only) */}
      {conversationType === ConversationTypeEnum.EMAIL &&
        signatureInsertError &&
        onDismissSignatureError && (
          <SignatureInsertErrorAlert
            errorType={signatureInsertError}
            onClose={onDismissSignatureError}
          />
        )}
    </>
  );
}
